import React from 'react';
import { SEOHead } from '@/components/seo';
import { Header, Footer } from '@/components/public';

const SITE_NAME = 'StartEase Agent';
const BASE_URL = 'https://starteaseai.com';

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <SEOHead
        title="Terms of Service | StartEase Agent"
        description="The terms governing your use of StartEase Agent — a business solutions platform connecting you with services, technology and specialist partners to start, operate and grow."
        siteName={SITE_NAME}
        canonical={`${BASE_URL}/terms`}
      />

      <Header />

      {/* Hero */}
      <section className="bg-[#070d0b] border-b border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400 mb-4">Legal</span>
          <h1 className="text-4xl md:text-5xl text-white mb-4">Terms of Service</h1>
          <p className="text-slate-400">Last updated: June 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 mb-6">
              By accessing or using StartEase Agent ("StartEase," "we," "our," or "us"), you accept and agree to be bound by these Terms of Service. If you do not agree, please do not use our website or services.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-6">
              StartEase is a business solutions platform that helps businesses find, implement and manage the services, technology and specialists they need to start, operate and grow globally. This includes solutions we deliver directly — such as company formation, compliance, and accounting across the United States, United Kingdom, United Arab Emirates, Singapore and Canada — as well as solutions provided by trusted third-party partners (for example, sales, payroll, finance and technology tools) that we recommend, connect you with, and can help you set up. StartEase is not a law firm, accounting firm, or financial adviser, and does not provide legal, tax, accounting or investment advice.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. User Responsibilities</h2>
            <p className="text-slate-600 mb-4">You agree to:</p>
            <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
              <li>Provide accurate and complete information for all services and filings</li>
              <li>Keep your account information secure and confidential</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
              <li>Comply with all applicable laws and regulations in every market you operate in</li>
              <li>Comply with the terms of any third-party partner whose product you adopt through us</li>
              <li>Not use our services or any connected partner service for any unlawful purpose</li>
            </ul>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Partner Solutions &amp; Referral Disclosure</h2>
            <p className="text-slate-600 mb-6">
              Some solutions featured or recommended by StartEase are provided by independent partner companies. When you choose a partner solution, your use of that product is governed by the partner's own terms and privacy policy, and the partner — not StartEase — is responsible for delivering it. StartEase may receive a referral or partner commission when you sign up for or purchase certain partner products through our links. This generally does not increase the price you pay. Any partner discount or offer is subject to the partner's terms and may change or end at any time.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Fees and Payment</h2>
            <p className="text-slate-600 mb-6">
              Fees for services StartEase delivers directly are displayed on our website or provided in a quote. Government, state and jurisdiction filing fees are separate and vary by location. Fees for third-party partner products are set and billed by the partner under their own terms. Service fees are non-refundable once work has commenced or a filing has been submitted, except where required by law. We may change our fees at any time with notice.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Service Limitations</h2>
            <p className="text-slate-600 mb-6">
              StartEase provides services, implementation support and referrals — not legal, tax, accounting or investment advice. For advice specific to your situation, please consult a licensed professional. Where we help implement a partner tool, StartEase is not responsible for the availability, performance, pricing or content of that third-party product. Processing and turnaround times are estimates and depend on government agencies and third parties.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 mb-6">
              All content and intellectual property on this website are owned by StartEase or its licensors. Third-party names, logos and trademarks shown on our site (including partner brands) remain the property of their respective owners and are used for identification purposes only. You may not reproduce, distribute, or create derivative works from our content without our express written permission.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Limitation of Liability</h2>
            <p className="text-slate-600 mb-6">
              StartEase shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services or any third-party partner service. Our total liability shall not exceed the amount paid by you to StartEase for the specific service in question.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Indemnification</h2>
            <p className="text-slate-600 mb-6">
              You agree to indemnify and hold harmless StartEase and its officers, directors, employees, and agents from any claims, damages, losses, or expenses arising from your use of our services or connected partner services, or your violation of these terms.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Termination</h2>
            <p className="text-slate-600 mb-6">
              We may terminate or suspend your access to our services at any time, without prior notice, for conduct that we believe violates these Terms of Service or is harmful to other users, us, or third parties.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. Governing Law</h2>
            <p className="text-slate-600 mb-6">
              These terms are governed by the laws applicable at StartEase's principal place of business, without regard to conflict of law provisions. Services delivered in a particular market may also be subject to the laws and regulations of that jurisdiction.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Changes to Terms</h2>
            <p className="text-slate-600 mb-6">
              We reserve the right to modify these terms at any time. We will notify users of significant changes via email or through our website. Your continued use of our services after such modifications constitutes acceptance of the updated terms.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Contact Information</h2>
            <p className="text-slate-600 mb-6">
              If you have any questions about these Terms of Service, please contact us at:<br />
              Email: hi@starteaseai.com<br />
              Phone: +1 (205) 624-5819<br />
              Website: starteaseai.com
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
